import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AuthContainer, 
  AuthHeader, 
  AuthTitle, 
  AuthSubtitle, 
  AuthCard, 
  TechGrid, 
  DataStream,
  FormGroup,
  Label,
  Input,
  Button,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  Logo,
  TechCircle,
  ProgressIndicator,
  ProgressStep,
  PasswordRequirements
} from './AuthStyles';
import { 
  EyeIcon, 
  SecurityIcon, 
  CompanyIcon, 
  SegmentIcon,
  WhatsAppIcon,
  buttonStyles,
  titleStyle,
  passwordToggleStyle
} from './icons';
import WhatsAppQRCode from './WhatsAppQRCode';
import authService from '../../services/authService';

const UserRegistration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dados do formulário
  const [companyName, setCompanyName] = useState('');
  const [segment, setSegment] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Validação de senha
  const [passwordValidation, setPasswordValidation] = useState({
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSymbol: false,
    hasMinLength: false
  });
  
  // Verificar se o usuário está autenticado
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/auth');
    }
  }, [navigate]);
  
  // Validar senha em tempo real
  useEffect(() => {
    setPasswordValidation({
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSymbol: /[!@#$%&*]/.test(password),
      hasMinLength: password.length >= 8
    });
  }, [password]);
  
  // Limpar mensagens de erro quando o usuário começa a digitar
  useEffect(() => {
    setError('');
  }, [companyName, segment, password, confirmPassword]);
  
  const validateStep = () => {
    switch (step) {
      case 1:
        if (!companyName.trim()) {
          setError('Por favor, informe o nome da empresa.');
          return false;
        }
        return true;
      case 2:
        if (!segment.trim()) {
          setError('Por favor, informe o segmento da empresa.');
          return false;
        }
        return true;
      case 3:
        if (!password) {
          setError('Por favor, crie uma senha.');
          return false;
        }
        
        const isPasswordValid = Object.values(passwordValidation).every(Boolean);
        if (!isPasswordValid) {
          setError('Sua senha não atende a todos os requisitos.');
          return false;
        }
        
        if (password !== confirmPassword) {
          setError('As senhas não coincidem.');
          return false;
        }
        return true;
      case 4:
        // WhatsApp QR code step doesn't require validation
        // User can proceed even if WhatsApp is not connected
        return true;
      default:
        return true;
    }
  };
  
  const handleNextStep = () => {
    if (validateStep()) {
      setStep(prevStep => prevStep + 1);
    }
  };
  
  const handlePrevStep = () => {
    setStep(prevStep => Math.max(1, prevStep - 1));
  };
  
  const handleSubmit = async () => {
    if (!validateStep()) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Obter dados do usuário existentes
      const userData = authService.getUserData();
      
      if (!userData) {
        setError('Dados do usuário não encontrados. Por favor, faça login novamente.');
        navigate('/auth');
        return;
      }
      
      // Preparar dados para envio
      const registrationData = {
        telefone: userData.telefone,
        nome_da_empresa: companyName,
        segmento: segment,
        senha: password
      };
      
      // Enviar dados para o webhook usando o serviço
      const response = await authService.registerUserData(registrationData);
      
      if (response === 'sucesso') {
        // Atualizar dados do usuário no localStorage
        const updatedUserData = {
          ...userData,
          nome_da_empresa: companyName,
          segmento: segment
        };
        
        authService.setUserData(updatedUserData);
        
        setSuccess('Cadastro concluído com sucesso!');
        
        // Redirecionar para a página de adição de produto após um breve delay
        setTimeout(() => {
          window.scrollTo(0, 0); // Rola a página para o topo
          navigate('/onboarding');
        }, 1500);
      } else {
        setError('Não foi possível concluir o cadastro. Por favor, tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao enviar dados de cadastro:', err);
      setError('Ocorreu um erro ao processar sua solicitação. Tente novamente mais tarde.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <FormGroup>
            <Label htmlFor="companyName">
              <CompanyIcon /> Nome da Empresa
            </Label>
            <Input
              id="companyName"
              type="text"
              placeholder="Digite o nome da sua empresa"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={isLoading}
            />
          </FormGroup>
        );
      case 2:
        return (
          <FormGroup>
            <Label htmlFor="segment">
              <SegmentIcon /> Segmento da Empresa
            </Label>
            <Input
              id="segment"
              type="text"
              placeholder="Ex: Imobiliária, Saúde, Educação, Tecnologia..."
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              disabled={isLoading}
            />
          </FormGroup>
        );
      case 3:
        return (
          <>
            <FormGroup>
              <Label htmlFor="password">
                <SecurityIcon /> Crie uma Senha
              </Label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Crie uma senha segura"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={passwordToggleStyle}
                >
                  <EyeIcon visible={showPassword} />
                </button>
              </div>
              
              <PasswordRequirements>
                <p>Sua senha deve conter:</p>
                <ul>
                  <li className={passwordValidation.hasUpperCase ? 'valid' : ''}>
                    Ao menos uma letra maiúscula
                  </li>
                  <li className={passwordValidation.hasLowerCase ? 'valid' : ''}>
                    Ao menos uma letra minúscula
                  </li>
                  <li className={passwordValidation.hasNumber ? 'valid' : ''}>
                    Ao menos um número
                  </li>
                  <li className={passwordValidation.hasSymbol ? 'valid' : ''}>
                    Ao menos um símbolo (!@#$%&*)
                  </li>
                  <li className={passwordValidation.hasMinLength ? 'valid' : ''}>
                    Ao menos 8 caracteres
                  </li>
                </ul>
              </PasswordRequirements>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="confirmPassword">
                <SecurityIcon /> Confirme sua Senha
              </Label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Digite a senha novamente"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  className={confirmPassword && password !== confirmPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={passwordToggleStyle}
                >
                  <EyeIcon visible={showConfirmPassword} />
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <ErrorMessage>As senhas não coincidem</ErrorMessage>
              )}
            </FormGroup>
          </>
        );
      case 4:
        return (
          <>
            <FormGroup>
              <h3 style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <WhatsAppIcon /> Conecte seu WhatsApp à BIANCA
              </h3>
              <WhatsAppQRCode />
            </FormGroup>
          </>
        );
      default:
        return null;
    }
  };
  
  return (
    <AuthContainer>
      <AuthHeader>
        <Logo src="/favicon.png" alt="BIANCA Logo" />
        <AuthTitle>BIANCA</AuthTitle>
        <AuthSubtitle>Complete seu cadastro para continuar</AuthSubtitle>
      </AuthHeader>
      
      <AuthCard>
        <TechGrid />
        <DataStream />
        <TechCircle size="200%" color="rgba(64, 128, 255, 0.2)" delay={0} />
        <TechCircle size="150%" color="rgba(64, 128, 255, 0.3)" delay={1} />
        <TechCircle size="120%" color="rgba(64, 128, 255, 0.1)" delay={2} />
        
        <h2 style={titleStyle}>
          {step === 1 && 'Informações da Empresa'}
          {step === 2 && 'Segmento de Atuação'}
          {step === 3 && 'Crie sua Senha'}
          {step === 4 && 'Conexão WhatsApp'}
        </h2>
        
        <ProgressIndicator currentStep={step}>
          <ProgressStep data-step="1" active={step >= 1} completed={step > 1}></ProgressStep>
          <ProgressStep data-step="2" active={step >= 2} completed={step > 2}></ProgressStep>
          <ProgressStep data-step="3" active={step >= 3} completed={step > 3}></ProgressStep>
          <ProgressStep data-step="4" active={step >= 4}></ProgressStep>
        </ProgressIndicator>
        
        {renderStepContent()}
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          {step > 1 ? (
            <Button 
              onClick={handlePrevStep} 
              disabled={isLoading}
              style={{...buttonStyles.base, ...buttonStyles.back}}
            >
              <span style={buttonStyles.text}>Voltar</span>
              <span style={buttonStyles.gradientLight}></span>
            </Button>
          ) : <div style={{ width: '48%' }}></div>}
          
          {step < 4 ? (
            <Button 
              onClick={handleNextStep} 
              disabled={isLoading}
              style={{...buttonStyles.base, ...buttonStyles.next}}
            >
              <span style={buttonStyles.text}>Próximo</span>
              <span style={buttonStyles.gradient}></span>
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={isLoading}
              style={{...buttonStyles.base, ...buttonStyles.submit}}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner /> Processando...
                </>
              ) : (
                <>
                  <span style={buttonStyles.text}>Concluir Cadastro</span>
                  <span style={buttonStyles.gradient}></span>
                </>
              )}
            </Button>
          )}
        </div>
      </AuthCard>
    </AuthContainer>
  );
};

export default UserRegistration;
